import { Button, Col, Form, notification, Row } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import SubHeader from '../../components/SubHeader';
import { showErrors } from '../../helpers/form';
import PermissoesHelper from '../../helpers/PermissoesHelper';
import { Estados, getSiglaFromEstados } from '../../helpers/uf';
import {
  validateClientesPagadores,
  validateEmail,
  validateForm,
  validateLogin,
  validatePass,
  validatePermissions,
} from '../../helpers/validators';
import { useAuth } from '../../security/authContext';
import apiAuth from '../../services/apiAuth';
import apiShipper from '../../services/apiShipper';
import InfoCliente from './InfoCliente';
import InfoUsuario from './InfoUsuario';
import PermissoesAcesso from './PermissoesAcessos';
import PreferenciasPlataforma from './PreferenciasPlataforma';
import { Card, GlobalStyle, Steps } from './styles';

const { Step } = Steps;

export default function CadastroUsuario() {
  const { userId } = useParams();
  const history = useHistory();
  const [form] = Form.useForm();
  const userLogado = useAuth();

  const [current, setCurrent] = useState(0);
  const [isEditingUser, setEditingUser] = useState(false);
  const [disableNext, setDisableNext] = useState(true);
  const [clientesSecundarios, setClientesSecundarios] = useState([]);
  const [clientesPagadores, setClientesPagadores] = useState([]);
  const [clientePerfil, setClientePerfil] = useState(true);
  const [cliente, setCliente] = useState([]);
  const [isValidPass, setIsValidPass] = useState(false);
  const [pass, setPass] = useState('');
  const [required, setRequired] = useState(true);
  const [idAddress, setIdAddress] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loadingButton, setLoadingButton] = useState(false);

  const [
    permissaoVisualizarTodosEstados,
    setPermissaoVisualizarTodosEstados,
  ] = useState(true);

  const podeVisualizarTodosOsEstados = user =>
    user.controle_embarque_estados_permitidos?.split(',').length ===
    Estados.length;

  const handleValidateForm = useCallback(() => {
    const isValid = validateForm(current, userId, form, clientePerfil);
    setDisableNext(isValid);
  }, [current, form, userId, clientePerfil]);

  const CLIENTE = [
    'painel-embarque:show',
    'painel-embarque:tracking',
    'dashboard-performance:aderencia-carregamento',
    'dashboard-performance:variacao-frete',
    'lotes:show',
    'lotes:edit',
    'lotes:create',
    'painel-embarque:contato',
    'mapa-gerenciamento:show',
    'precificacao:show',
  ];

  const steps = useMemo(() => {
    const stepsData = [
      {
        title: 'Informações do Usuário',
        keyName: 'infoUser',
        content: (
          <InfoUsuario
            form={form}
            setClientePerfil={setClientePerfil}
            setIsValidPass={setIsValidPass}
            setPass={setPass}
            required={required}
            validateForm={handleValidateForm}
            isEditingUser={isEditingUser}
          />
        ),
      },
      {
        title: 'Informações do Cliente',
        keyName: 'infoCliente',
        content: (
          <InfoCliente
            form={form}
            cliente={cliente}
            setCliente={setCliente}
            clientesSecundarios={clientesSecundarios}
            setClientesSecundarios={setClientesSecundarios}
            clientesPagadores={clientesPagadores}
            setClientesPagadores={setClientesPagadores}
            isEditingUser={isEditingUser}
          />
        ),
      },
      {
        title: 'Permissões e Acessos',
        keyName: 'permissoesAcessos',
        content: (
          <PermissoesAcesso
            selectedPermissions={selectedPermissions}
            setSelectedPermissions={setSelectedPermissions}
            permissaoVisualizarTodosEstados={permissaoVisualizarTodosEstados}
            setPermissaoVisualizarTodosEstados={
              setPermissaoVisualizarTodosEstados
            }
          />
        ),
      },
      {
        title: 'Preferências da Plataforma',
        keyName: 'PreferenciasPlataforma',
        content: (
          <PreferenciasPlataforma
            selectedPermissions={selectedPermissions}
            setSelectedPermissions={setSelectedPermissions}
          />
        ),
      },
    ];

    if (!clientePerfil) {
      stepsData.splice(1, 1); // Remove o step InfoCliente
    }

    return stepsData;
  }, [
    form,
    required,
    cliente,
    clientesSecundarios,
    clientesPagadores,
    selectedPermissions,
    permissaoVisualizarTodosEstados,
    clientePerfil,
    isEditingUser,
    handleValidateForm,
  ]);

  useEffect(() => {
    handleValidateForm();
  }, [current]);

  useEffect(() => {
    async function onUnitNovoUsuario() {
      if (!userId) {
        if (userLogado.user.profile === 'CLIENTE') {
          if (userLogado.user.cliente_id_secundarios === null) {
            const clientePrimario = await apiShipper.get(
              `/clientes/${userLogado.user.cliente_id}`
            );

            setCliente([clientePrimario.data]);

            form.setFieldsValue({
              idCliente: clientePrimario.data.id,
            });
          }

          const permissoes = await PermissoesHelper.getPermissoes(false);
          setSelectedPermissions(
            permissoes.reduce((acumulador, permissao) => {
              if (CLIENTE.includes(permissao.rule)) {
                acumulador.push({ id: permissao.id });
              }
              return acumulador;
            }, [])
          );
        }
      }
    }

    onUnitNovoUsuario();
  }, []);

  useEffect(() => {
    async function onInit() {
      if (!userId) return;

      setEditingUser(true); // ativo o modo edição
      setRequired(false); // desabilita a necessidade de preencher o password
      setIsValidPass(true); // aceita a senha como se tivesse correta

      // consulta os dados do usuário
      const anUser = await apiAuth.get(`/users/${userId}`);
      const user = anUser.data;
      const address = user.addresses[0];
      const clientePrimario = await apiShipper.get(
        `/clientes/${user.cliente_id}`
      );

      setCliente([clientePrimario.data]);
      if (user.permissions && user.permissions.length > 0) {
        setSelectedPermissions(user.permissions);
      }

      if (user.cliente_id_secundarios) {
        const { data } = await apiShipper.get('/clientes', {
          params: {
            idClienteSecundarios: user.cliente_id_secundarios,
            size: user.cliente_id_secundarios.split(',').length,
          },
        });

        setClientesSecundarios(data.content);
      }

      if (user.clientes_pagadores) {
        const { data } = await apiShipper.get('/clientes/tmov', {
          params: {
            id_clientes: user.clientes_pagadores,
          },
        });

        setClientesPagadores(data.content);
      }

      setClientePerfil(user.profile === 'CLIENTE');
      setIdAddress(address?.id ?? null);
      setPermissaoVisualizarTodosEstados(podeVisualizarTodosOsEstados(user));

      let estadosPermitidos = user.controle_embarque_estados_permitidos
        ? user.controle_embarque_estados_permitidos.split(',')
        : [];

      estadosPermitidos = estadosPermitidos.map(
        siglaEstado => Estados.find(uf => uf.sigla === siglaEstado)?.nome
      );
      // preenche os inputs com as informações recuperada
      form.setFieldsValue({
        nomeUsuario: user.name,
        loginUsuario: user.login,
        celUsuario: user.phone_number,
        emailUsuario: user.email,
        idCliente: clientePrimario.data.id,
        cpfUsuario: user.cpf,
        perfilUsuario: user.profile,
        passConfirm: '',
        active: user.active,
        lograUsuario: address?.street,
        cidadeUsuario: address?.city,
        bairroUsuario: address?.neighborhood,
        numLograUsuario: address?.number,
        estadoUsuario: address?.state,
        cepUsuario: address?.zip_code,
        visualizarTodosEstados: podeVisualizarTodosOsEstados(user)
          ? 'sim'
          : 'nao',
        estadosVisiveis: estadosPermitidos,
      });

      handleValidateForm();
    }

    onInit();
  }, [form, userId, clientePerfil]);

  const handleSubmitUser = async values => {
    if (validatePermissions(selectedPermissions)) return;

    const estadosInputs = getSiglaFromEstados(values.estadosVisiveis);

    try {
      setLoadingButton(true);
      const clientes_pagadores =
        clientesPagadores.length > 0
          ? clientesPagadores.map(clientePag => clientePag.id)
          : null;

      // Corpo da requisição para cadastrar/editar usuário
      const valuesToUpdate = {
        name: values.nomeUsuario,
        login: values.loginUsuario,
        phone_number: values.celUsuario,
        email: values.emailUsuario,
        cliente_id: values.idCliente,
        cpf: values.cpfUsuario,
        profile: values.perfilUsuario,
        active: values.active,
        controle_embarque_estados_permitidos: estadosInputs,
        addresses: [
          {
            street: values.lograUsuario,
            city: values.cidadeUsuario,
            neighborhood: values.bairroUsuario,
            number: values.numLograUsuario,
            state: values.estadoUsuario,
            zip_code: values.cepUsuario,
          },
        ],
        users_permissions: selectedPermissions,
        cliente_id_secundarios:
          clientesSecundarios.map(clienteSec => clienteSec.id).toString() ||
          null,
        clientes_pagadores,
      };

      if (isEditingUser) {
        valuesToUpdate.addresses[0].id = idAddress;
        if (values.passConfirm) {
          valuesToUpdate.password = values.passConfirm;
        }

        await apiAuth.patch(`/users/${userId}`, valuesToUpdate);
      } else {
        valuesToUpdate.password = values.passConfirm;

        await apiAuth.post('/users', valuesToUpdate);
      }

      notification.success({
        message: 'Tudo certo',
        description: isEditingUser
          ? 'O usuário foi atualizado com sucesso.'
          : 'O usuário foi cadastrado com sucesso.',
      });

      history.push('/usuarios');
    } catch (error) {
      setLoadingButton(false);
      const errors = error?.response?.data?.errors
        ? Object.values(error?.response?.data?.errors)
        : null;

      showErrors(errors);
    }
  };

  const previousStep = () => {
    if (current === 0) return history.push('/usuarios');
    setCurrent(current - 1);
  };

  const nextStep = () => {
    const emailValue = form.getFieldValue('emailUsuario');
    const loginValue = form.getFieldValue('loginUsuario');
    const validatePassParams = {
      password: pass,
      passwordConfirm: form.getFieldValue('passConfirm'),
      isValidPass,
    };

    const validateClientesPagadoresParams = {
      cliente,
      clientesSecundarios,
      clientesPagadores,
    };

    if (
      current === 0 &&
      (!validatePass(validatePassParams) ||
        !validateEmail(emailValue) ||
        !validateLogin(loginValue))
    ) {
      return;
    }

    if (
      current === 1 &&
      !validateClientesPagadores(validateClientesPagadoresParams)
    ) {
      return;
    }

    if (current === 2 && validatePermissions(selectedPermissions)) return;

    setCurrent(current + 1);
  };

  const breadcrumbs = [
    {
      path: '/usuarios',
      breadcrumbName: 'Parametrização',
    },
    {
      breadcrumbName: userId ? 'Edição de Usuário' : 'Cadastro de Usuário',
    },
  ];

  return (
    <>
      <GlobalStyle />
      <SubHeader breadcrumbs={breadcrumbs} />
      <Card bordered={false} className="pt-0 height-85">
        <Row>
          <Col span={12} offset={6}>
            <Steps
              current={current}
              labelPlacement="vertical"
              size="small"
              className="customSteps"
            >
              {steps.map(item => (
                <Step key={item.title} title={item.title} />
              ))}
            </Steps>
          </Col>
        </Row>

        <Form
          noValidate
          onFinish={handleSubmitUser}
          onValuesChange={handleValidateForm}
          form={form}
          autoComplete="off"
          initialValues={{
            perfilUsuario: 'CLIENTE',
            active: true,
          }}
        >
          {steps.map((item, index) => (
            <Form.Item
              key={item.title}
              className={`steps-content ${index !== current && 'hidden'}`}
            >
              {item.content}
            </Form.Item>
          ))}

          <Row gutter={12} justify="end">
            <Button className="margin-sides" onClick={previousStep}>
              Voltar
            </Button>
            {current === steps.length - 1 && (
              <Button
                type="primary"
                htmlType="submit"
                disabled={disableNext}
                loading={loadingButton}
              >
                {isEditingUser ? 'Editar' : 'Salvar'}
              </Button>
            )}
            {current < steps.length - 1 && (
              <Button type="primary" disabled={disableNext} onClick={nextStep}>
                Continuar
              </Button>
            )}
          </Row>
        </Form>
      </Card>
    </>
  );
}
